import React from 'react';
import { View, StyleSheet, TouchableOpacity, ViewStyle, TextStyle } from 'react-native';
import { Text } from '@/components/common/text';
import { SVG_COMPONENTS } from '@/assets/admin';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';

type AdminErrorStateProps = {
  title: string;
  message?: string;
  retryCount?: number;
  canRetry?: boolean;
  onRetry?: () => void;
  containerStyle?: ViewStyle;
  titleStyle?: TextStyle;
  messageStyle?: TextStyle;
  retryButtonText?: string;
};

/**
 * Inline error state surface — pair with hooks/data branches that need to
 * surface a recoverable failure without unmounting the screen. For uncaught
 * exceptions use `AdminErrorBoundary` instead.
 */
export const AdminErrorState: React.FC<AdminErrorStateProps> = ({
  title,
  message,
  retryCount,
  canRetry,
  onRetry,
  containerStyle,
  titleStyle,
  messageStyle,
  retryButtonText = 'Try Again',
}) => {
  const styles = useStyles();
  const { semantic } = useAdminColors();

  return (
    <View style={[styles.errorContainer, containerStyle]}>
      <SVG_COMPONENTS.X_CIRCLE width={48} height={48} color={semantic.status.error} />
      <Text scale="content" style={[styles.errorTitle, titleStyle]}>{title}</Text>
      {!!message && <Text scale="content" style={[styles.errorMessage, messageStyle]}>{message}</Text>}
      {typeof retryCount === 'number' && retryCount > 0 && (
        <Text style={styles.retryInfo}>Retry attempts: {retryCount}</Text>
      )}
      {canRetry && !!onRetry && (
        <TouchableOpacity style={styles.retryButton} onPress={onRetry}>
          <SVG_COMPONENTS.CHEVRON_RIGHT width={16} height={16} color="#FFFFFF" />
          <Text style={styles.retryButtonText}>{retryButtonText}</Text>
        </TouchableOpacity>
      )}
    </View>
  );
};

const useStyles = () =>
  useThemedStyles(({ components, colors }) => {
    const admin = components.admin;
    return StyleSheet.create({
      errorContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        padding: 24,
      },
      errorTitle: {
        fontSize: 18,
        fontWeight: '600',
        color: admin.dataTable.text,
        marginTop: 16,
        marginBottom: 8,
        textAlign: 'center',
      },
      errorMessage: {
        fontSize: 14,
        color: colors.text.dim,
        textAlign: 'center',
        marginBottom: 16,
        lineHeight: 20,
        maxWidth: 300,
      },
      retryInfo: {
        fontSize: 12,
        color: colors.text.muted,
        textAlign: 'center',
        marginBottom: 16,
        fontStyle: 'italic',
      },
      retryButton: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: admin.formInput.borderFocused,
        paddingHorizontal: 20,
        paddingVertical: 12,
        borderRadius: 8,
        gap: 8,
      },
      retryButtonText: {
        color: '#FFFFFF',
        fontSize: 16,
        fontWeight: '600',
      },
    });
  });

export default AdminErrorState;
